import NiceModal, { useModal } from "@ebay/nice-modal-react";
import ModalBase from "modals/base/ModalBase";
import { useRef } from "react";
import P from "components/P";
import Dummy from "components/Dummy";
import { BrowserDetectContext } from "providers/BrowserEventProvider";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AuthReducer } from "shared/redux/reducers/auth/AuthReducer";
import { OrderInfoReducer } from "shared/redux/reducers/shopping/OrderInfoReducer";
import { removeLocalStorage } from "shared/utils/Utils";
import { AuthContext } from "providers/AuthProvider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { requester } from "App";
import biometricService from "shared/BiometricService";
import { toast } from "react-toastify";
import useSmartNavigate from "hooks/useSmartNavigate";
import { devLog } from "utils/logger";

// NiceModal.create을 사용하여 모달 컴포넌트 생성
const HamburgerMenu = NiceModal.create(({ storeId, userName, isMobile }) => {
    const modal = useModal();
    const dispatch = useDispatch();
    const [biometricStatus, setBiometricStatus] = useState({
        supported: false,
        enabled: false,
        loading: true
    });

    useEffect(() => {
        // 생체인증 지원 여부 확인
        if (isMobile) {
            const isSupported = biometricService.isSupported();
            const storedStatus = localStorage.getItem('puffu_biometric_supported');
            const isEnabled = storedStatus === 'true';

            setBiometricStatus({
                supported: isSupported,
                enabled: isEnabled,
                loading: false
            });
        }
    }, [isMobile]);

    // 생체인증 등록 시작 함수
    const startBiometricRegistration = async () => {
        devLog("🔍 [HamburgerMenu] startBiometricRegistration 호출됨");
        devLog("🔍 [HamburgerMenu] 생체인증 지원 상태:", biometricStatus.supported);
        devLog("🔍 [HamburgerMenu] 플랫폼:", biometricService.getPlatform());
        devLog("🔍 [HamburgerMenu] 네이티브 브릿지 상태:", {
            hasWebkit: !!window.webkit,
            hasBRIDGE: !!window.BRIDGE,
            webkitBridge: !!window.webkit?.messageHandlers?.BRIDGE,
            userName: userName
        });

        if (!biometricStatus.supported) {
            devLog("🔍 [HamburgerMenu] 생체인증 미지원으로 리턴");
            return;
        }

        // 🔥 플랫폼별 메시지는 컴포넌트 레벨에서 정의됨

        // 🔥 AuthenticationFlow 방식: hasRegisteredKey() 체크 생략하고 바로 등록 모달 표시
        devLog("🔍 [HamburgerMenu] AuthenticationFlow 방식으로 바로 등록 모달 표시");
        devLog("🔍 [HamburgerMenu] hasRegisteredKey() 체크 생략 (무한 대기 문제 해결)");
        
        try {
            // 🔥 기존 키 확인을 생략하고 바로 등록 모달 표시 (AuthenticationFlow 방식)
            devLog("🔍 [HamburgerMenu] 등록 확인 모달 표시 시작");
            NiceModal.show("biometricConfirm", {
                title: getBiometricLabel(),
                content: getRegistrationMessage(),
                confirmText: "등록하기",
                cancelText: "취소",
                onConfirm: () => {
                    devLog("🔍 [HamburgerMenu] 모달에서 '등록하기' 클릭됨");
                    registerBiometric();
                }
            });
            devLog("🔍 [HamburgerMenu] 등록 확인 모달 표시 완료");
        } catch (error) {
            console.error("🔍 [HamburgerMenu] 모달 표시 오류:", error);
            devLog("🔍 [HamburgerMenu] 오류 발생으로 인한 토스트 메시지 표시");
            toast.error("생체인증 등록 준비 중 오류가 발생했습니다.");
        }
    };

    // 🔥 AuthenticationFlow 검증된 생체인증 등록 로직 적용 (iOS + Android 통합)
    const registerBiometric = async () => {
        devLog("🔍 [HamburgerMenu] registerBiometric 함수 진입");
        devLog("🔍 [HamburgerMenu] 생체인증 지원 상태:", biometricStatus.supported);
        
        if (!biometricStatus.supported) {
            devLog("🔍 [HamburgerMenu] 생체인증 미지원으로 에러 표시");
            const getNotSupportedMessage = () => {
                if (isIOSDevice()) {
                    return "이 기기는 Face ID를 지원하지 않습니다";
                } else {
                    return "이 기기는 지문인증을 지원하지 않습니다";
                }
            };
            toast.error(getNotSupportedMessage());
            return;
        }

        devLog("🔍 [HamburgerMenu] 로딩 상태 설정");
        setBiometricStatus(prev => ({ ...prev, loading: true }));

        try {
            devLog("🔍 [HamburgerMenu-registerBiometric] 생체인증 등록 시작", { 
                platform: biometricService.getPlatform(),
                userName: userName,
                biometricServiceExists: !!biometricService,
                registerBiometricKeyExists: !!biometricService.registerBiometricKey
            });
            
            // 최대 시도 횟수와 현재 시도 횟수 설정
            const maxAttempts = 3;
            let currentAttempt = 0;
            let registrationSuccess = false;

            // 🔥 플랫폼별 메시지를 반환하는 헬퍼 함수들을 여기에 정의합니다.
            const getRetryMessage = (current, max) => {
                const platformLabel = getBiometricLabel();
                return `${platformLabel} 등록에 실패했습니다. 다시 시도합니다. (${current}/${max})`;
            };

            const getMaxAttemptsMessage = () => {
                const platformLabel = getBiometricLabel();
                return `${platformLabel} 등록에 여러 번 실패했습니다. 나중에 다시 시도해주세요.`;
            };

            const attemptBiometricRegistration = async () => {
                try {
                    currentAttempt++;
                    devLog("[HamburgerMenu-registerBiometric] 생체인증 등록 시도", { currentAttempt, maxAttempts });

                    const userId = biometricService.getPhoneNumber();
                    if (!userId) {
                        throw new Error("사용자 정보를 찾을 수 없습니다. 로그인을 다시 시도해주세요.");
                    }

                    // 🔥 이미 등록된 키 있는지 체크 (AuthenticationFlow에는 없지만 안전장치로 추가)
                    try {
                        devLog("🔍 [HamburgerMenu] registerBiometric 내부에서 기존 키 확인 시도");
                        const hasExistingKey = await Promise.race([
                            biometricService.hasRegisteredKey(),
                            new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 2000))
                        ]);
                        
                        if (hasExistingKey) {
                            devLog("🔍 [HamburgerMenu] registerBiometric에서 기존 키 발견, 상태 업데이트 후 리턴");
                            setBiometricStatus(prev => ({
                                ...prev,
                                enabled: true,
                                loading: false
                            }));
                            localStorage.setItem('puffu_biometric_supported', 'true');
                            toast.info(getAlreadyRegisteredMessage(), {
                                autoClose: 2000, position: toast.POSITION.BOTTOM_CENTER
                            });
                            return;
                        }
                    } catch (timeoutOrError) {
                        devLog("🔍 [HamburgerMenu] 기존 키 확인 타임아웃/오류, 새 등록 진행:", timeoutOrError.message);
                        // 타임아웃이나 오류 시 새 등록 진행
                    }

                    // 생체인증 키 등록 진행 (AuthenticationFlow와 동일)
                    devLog("🔍 [HamburgerMenu] registerBiometricKey() 호출 시작", { userId });
                    devLog("🔍 [HamburgerMenu] 네이티브 브릿지 최종 확인:", {
                        hasWebkit: !!window.webkit,
                        hasWebkitBridge: !!window.webkit?.messageHandlers?.BRIDGE,
                        hasBRIDGE: !!window.BRIDGE,
                        platform: biometricService.getPlatform()
                    });
                    
                    const biometricKeyResult = await biometricService.registerBiometricKey(userId);
                    devLog("🔍 [HamburgerMenu-registerBiometric] 생체인증 키 등록 결과", { biometricKeyResult });

                    if (biometricKeyResult && biometricKeyResult.publicKey) {
                        // 🔥 AuthenticationFlow 방식으로 생체인증 정보 구성
                        const biometricData = {
                            biometricPublicKey: biometricKeyResult.publicKey,
                            biometricAlgorithm: biometricKeyResult.algorithm || "EC",
                            biometricEnabled: true,
                            biometricKeySignature: calculateKeySignature(biometricKeyResult.publicKey),
                            biometricPlatform: biometricService.getPlatform()
                        };

                        devLog("[HamburgerMenu-registerBiometric] 생체인증 정보 생성 완료", biometricData);

                        // 서버에 생체인증 정보 업데이트 (AuthenticationFlow와 동일한 방식)
                        const updateData = {
                            userName: userName,
                            ...biometricData
                        };

                        devLog("[HamburgerMenu-registerBiometric] 서버 업데이트 요청", updateData);

                        // 🔥 AuthenticationFlow와 동일한 API 호출
                        requester.checkMemberBiometricStatus(updateData, (result) => {
                            devLog("[HamburgerMenu-registerBiometric] 서버 업데이트 응답", result);

                            if (result.code === 0) {
                                devLog("[HamburgerMenu-registerBiometric] 생체인증 등록 완전 성공");

                                // 상태 업데이트
                                setBiometricStatus(prev => ({
                                    ...prev,
                                    enabled: true,
                                    loading: false
                                }));

                                // 로컬 스토리지에 상태 저장
                                localStorage.setItem('puffu_biometric_supported', 'true');

                                // 🔥 플랫폼별 성공 메시지 (이미 구현된 함수 사용)
                                const getSuccessMessage = () => {
                                    if (isIOSDevice()) {
                                        return "생체인증이 성공적으로 등록되었습니다!"; // iOS 통일
                                    } else {
                                        return "지문인증이 성공적으로 등록되었습니다!"; // Android
                                    }
                                };

                                toast.success(getSuccessMessage(), {
                                    autoClose: 2000, position: toast.POSITION.BOTTOM_CENTER
                                });
                            } else {
                                console.error("[HamburgerMenu-registerBiometric] 서버 업데이트 실패", result);
                                setBiometricStatus(prev => ({ ...prev, loading: false }));
                                throw new Error("서버 업데이트 실패");
                            }
                        });
                    } else {
                        console.error("[HamburgerMenu-registerBiometric] 생체인증 키 생성 실패");
                        throw new Error("생체인증 키 생성 실패");
                    }
                } catch (error) {
                    console.error("[HamburgerMenu-registerBiometric] 생체인증 등록 오류:", error);

                    // 🔥 사용자 취소를 가장 먼저 확인하고, 오류로 간주하지 않고 조용히 종료합니다.
                    if (error && (error.message === 'USER_CANCEL' || error.message === 'USER_CANCELED' || error.message.includes('cancel'))) {
                        devLog("[HamburgerMenu] 사용자가 생체인증 등록을 취소했습니다.");
                        toast.info("생체인증 등록이 취소되었습니다.", { autoClose: 2000 });
                        // 여기서 함수 실행을 완전히 중단합니다.
                        return;
                    }

                    // 🔥 실제 오류인 경우에만 재시도 또는 실패 처리를 진행합니다.
                    if (currentAttempt < maxAttempts) {
                        toast.warning(getRetryMessage(currentAttempt, maxAttempts));
                        setTimeout(() => {
                            attemptBiometricRegistration();
                        }, 1500);
                    } else {
                        toast.error(getMaxAttemptsMessage());
                    }
                }
            };

            // 🔥 AuthenticationFlow와 동일 - 첫 번째 시도 시작
            await attemptBiometricRegistration();

        } catch (error) {
            console.error("[HamburgerMenu-registerBiometric] 최종 오류:", error);
            setBiometricStatus(prev => ({ ...prev, loading: false }));
            
            const getFinalErrorMessage = () => {
                if (isIOSDevice()) {
                    return "생체인증 등록 중 오류가 발생했습니다.";
                } else {
                    return "지문인증 등록 중 오류가 발생했습니다.";
                }
            };

            toast.error(getFinalErrorMessage(), {
                autoClose: 3000, position: toast.POSITION.BOTTOM_CENTER
            });
        }
    };

    // 🔥 AuthenticationFlow와 동일한 공개키 해시 계산 함수
    const calculateKeySignature = (publicKeyBase64) => {
        try {
            // Base64 인코딩된 공개키의 앞부분만 사용하여 간단한 서명 생성 (AuthenticationFlow와 동일)
            return publicKeyBase64.substring(0, 64);
        } catch (error) {
            console.error("[calculateKeySignature] 키 시그니처 생성 오류:", error);
            return publicKeyBase64; // 오류 시 원본 키 반환 (AuthenticationFlow와 동일)
        }
    };

    // 🔥 플랫폼별 생체인증 UI 설정 함수들
    const isIOSDevice = () => {
        return /iPad|iPhone|iPod/.test(navigator.userAgent);
    };

    // 🔥 플랫폼별 메시지 함수들 (컴포넌트 레벨로 이동)
    const getAlreadyRegisteredMessage = () => {
        if (isIOSDevice()) {
            return "생체인증이 이미 등록되어 있습니다."; // iOS 통일
        } else {
            return "지문인증이 이미 등록되어 있습니다."; // Android
        }
    };

    const getRegistrationMessage = () => {
        if (isIOSDevice()) {
            return "생체인증을 등록하면 빠르고 안전하게 로그인할 수 있습니다. 등록하시겠습니까?"; // iOS 통일
        } else {
            return "지문인증을 등록하면 지문으로 빠르고 안전하게 로그인할 수 있습니다. 등록하시겠습니까?"; // Android
        }
    };

    const getBiometricLabel = () => {
        if (isIOSDevice()) {
            return "생체인증 등록"; // iOS 통일
        } else {
            return "지문인증 등록"; // Android
        }
    };

    const getBiometricIcon = () => {
        if (isIOSDevice()) {
            return fas.faUserCheck; // iOS Face ID 아이콘 통일
        } else {
            return fas.faFingerprint; // Android 지문 아이콘
        }
    };

    const getBiometricDescription = () => {
        if (isIOSDevice()) {
            return "생체인증으로 빠르고 안전한 로그인"; // iOS 통일
        } else {
            return "지문 인식으로 간편하고 안전한 로그인"; // Android
        }
    };

    // 로그아웃 확인 모달 표시
    const onSignoutClick = () => {
        NiceModal.show("logout", {
            message: "로그아웃 하시겠습니까?",
            confirmText: "확인",
            cancelText: "취소",
            onConfirm: onConfirmLogout,
            onCancel: () => { }
        });
    };

    // 로그아웃 실행
    const onConfirmLogout = () => {
        removeLocalStorage("token");
        dispatch(AuthReducer.actions.reset());
        dispatch(OrderInfoReducer.actions.reset());
        window.location.replace(`/ws/${storeId}/mobile`);
        modal.hide(); // 햄버거 메뉴 닫기
    };

    // 회원탈퇴 페이지로 이동
    const onWithdrawalClick = () => {
        window.location.href = `/ws/${storeId}/mobile/settings/withdrawal`;
        modal.hide(); // 햄버거 메뉴 닫기
    };

    // 페이지 이동 함수
    const navigateTo = (path) => {
        window.location.replace(`/ws/${storeId}/mobile/${path}`);
        modal.hide(); // 햄버거 메뉴 닫기
    };

    // 메뉴 섹션 정의
    const settingItems = [
        // {
        //     title: "주요 메뉴",
        //     items: [
        //         { label: "홈", icon: fas.faHome, onClick: () => navigateTo("main") },
        //         { label: "내 주변", icon: fas.faMapMarkerAlt, onClick: () => navigateTo("map") },
        //         { label: "쇼핑", icon: fas.faShoppingBag, onClick: () => navigateTo("shopping") },
        //         { label: "주문내역", icon: fas.faHistory, onClick: () => navigateTo("orderHistory") }
        //     ]
        // },
        {
            title: "이용 설정",
            items: [
                { label: "알림 설정", icon: fas.faBell, onClick: () => navigateTo("settings/notifications") },
                { label: "설정", icon: fas.faCog, onClick: () => navigateTo("settings") }
            ]
        },
        {
            title: "고객 지원",
            items: [
                { label: "이용약관", icon: fas.faFileAlt, onClick: () => navigateTo("settings/policy") },
                { label: "개인정보 처리방침", icon: fas.faUserShield, onClick: () => navigateTo("settings/privacy") }
            ]
        },
        ...(isMobile && 
            biometricStatus.supported && 
            !biometricStatus.enabled && 
            !biometricStatus.loading ? [{
            title: "보안",
            items: [
                {
                    label: getBiometricLabel(),
                    icon: getBiometricIcon(),
                    onClick: startBiometricRegistration,
                    description: getBiometricDescription()
                }
            ]
        }] : [])
    ];

    return (
        <ModalBase
            width="85%"
            maxWidth="320px"
            height="100%"
            slideLeft={true}
            clickOutsideToClose={true}
            withHeader={true}
            padding="0"
        >
            <div style={{ height: "100%", backgroundColor: "#fff", overflowY: "auto" }}>
                <div style={{
                    padding: "24px 20px",
                    borderBottom: "1px solid #f5f5f5",
                    backgroundColor: "#fff"
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        marginBottom: "8px"
                    }}>
                        <div style={{
                            width: "54px",
                            height: "54px",
                            borderRadius: "50%",
                            backgroundColor: "var(--main-color, #5471e6)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center"
                        }}>
                            <FontAwesomeIcon
                                icon={fas.faUser}
                                style={{
                                    fontSize: "20px",
                                    color: "#fff"
                                }}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <P size={20} weight={700} style={{ marginBottom: "4px" }}>
                                {userName} 님
                            </P>
                            <P size={14} color="#666">
                                환영합니다!
                            </P>
                        </div>
                    </div>
                </div>

                <div style={{ padding: "16px 16px 80px 16px" }}>
                    {settingItems.map((section, index) => (
                        <div key={index} style={{ marginBottom: "24px" }}>
                            <P size={14} color={"#666"} weight={600} style={{ marginBottom: "8px", paddingLeft: "4px" }}>
                                {section.title}
                            </P>
                            <div style={{
                                backgroundColor: "#fff",
                                borderRadius: "12px",
                                overflow: "hidden",
                                border: "1px solid #f5f5f5"
                            }}>
                                {section.items.map((item, itemIndex) => (
                                    <div
                                        key={itemIndex}
                                        onClick={item.disabled ? undefined : item.onClick}
                                        style={{
                                            padding: "12px 16px",
                                            display: "flex",
                                            alignItems: "center",
                                            cursor: item.disabled ? "default" : "pointer",
                                            borderBottom: itemIndex < section.items.length - 1 ? "1px solid #f5f5f5" : "none",
                                            backgroundColor: "#fff",
                                            opacity: item.disabled ? 0.6 : 1
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={item.icon}
                                            style={{
                                                width: "20px",
                                                color: item.color || "#333",
                                                marginRight: "12px"
                                            }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            <P
                                                size={15}
                                                weight={500}
                                                color={item.color || "#333"}
                                            >
                                                {item.label}
                                            </P>
                                            {item.description && (
                                                <P size={12} color="#999">
                                                    {item.description}
                                                </P>
                                            )}
                                        </div>
                                        <FontAwesomeIcon
                                            icon={fas.faChevronRight}
                                            style={{
                                                color: "#999",
                                                fontSize: "14px"
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </ModalBase>
    );
});

export default HamburgerMenu;